  // src/api/notesApi.js
  import { getCurrentUser } from '../components/utils/authService'; 

  const API_BASE_URL = '/api';

  /**
   * @typedef {Object} Note
   * @property {number} id - Note ID
   * @property {string} subId - User ID
   * @property {string} noteType - Type of note
   * @property {string} title - Note title
   * @property {string} note - Note content
   * @property {string} tags - Note tags
   * @property {number} primary_category_id - Primary category ID
   * @property {string} secondary_categories - JSON string of secondary category IDs
   */

  const handleApiError = async (response, customMessage) => {
    if (!response.ok) {
      let errorMessage = customMessage;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch (e) {
        errorMessage = `${customMessage}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }
  };

  /**
   * Fetch all notes for the current user
   * @returns {Promise<Note[]>} Array of notes
   */
  export const fetchNotes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/notes`, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      
      await handleApiError(response, 'Failed to fetch notes');
      return await response.json();
    } catch (error) {
      console.error('Error fetching notes:', error);
      throw error;
    }
  };

  /**
   * Fetch a specific note by ID
   * @param {number} noteId - The ID of the note to fetch
   * @returns {Promise<Note>} The requested note
   */
  export const fetchNote = async (noteId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/notes/${noteId}`, {
        credentials: 'include'
      });
      
      await handleApiError(response, 'Failed to fetch note');
      return await response.json();
    } catch (error) {
      console.error('Error fetching note:', error);
      throw error;
    }
  };

  /**
   * Create a new note
   * @param {string} userId - The user ID
   * @param {number} defaultCategoryId - The default category ID
   * @returns {Promise<Note>} The created note
   */
  export const createNote = async (userId, defaultCategoryId) => {
    const newNote = {
      subId: userId,
      noteType: 'text',
      title: 'Untitled',
      note: '',
      tags: '',
      primary_category_id: defaultCategoryId,
      secondary_categories: '[]'
    };

    try {
      const response = await fetch(`${API_BASE_URL}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(newNote)
      });

      if (!response.ok) {
        throw new Error('Failed to create note');
      }

      const createdNote = await response.json();
      return createdNote;
    } catch (error) {
      console.error('Error in createNote:', error);
      throw error;
    }
  };

  /**
   * Update an existing note
   * @param {number} noteId - The ID of the note to update
   * @param {Object} noteData - The note data to update
   * @returns {Promise<Note>} The updated note
   */
  export const updateNote = async (noteId, noteData) => {
    try {
      // Extract only the fields needed by the backend
      const dataToSend = {
        subId: noteData.subId,
        noteType: noteData.noteType,
        title: noteData.title,
        note: noteData.note,
        tags: noteData.tags || '',
        primary_category_id: noteData.primary_category_id,
        secondary_categories: noteData.secondary_categories || '[]',
        parent_id: noteData.parent_id
      };
      
      console.log(`Sending update to server for note ${noteId}:`, dataToSend);
      
      const response = await fetch(`${API_BASE_URL}/notes/${noteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(dataToSend)
      });

      await handleApiError(response, 'Failed to update note');
      return await response.json();
    } catch (error) {
      console.error('Error updating note:', error);
      throw error;
    }
  };

  /**
   * Delete a note
   * @param {number} noteId - The ID of the note to delete
   * @returns {Promise<boolean>} True if deletion was successful
   */
  export const deleteNote = async (noteId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/notes/${noteId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      await handleApiError(response, 'Failed to delete note');
      return true;
    } catch (error) {
      console.error('Error deleting note:', error);
      throw error;
    }
  };

  /**
   * Fetch images for a specific note
   * @param {number} noteId - The ID of the note to fetch images for
   * @returns {Promise<Array>} The images associated with the note
   */
  export const fetchNoteImages = async (noteId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/notes/${noteId}/images`, {
        credentials: 'include'
      });
      
      await handleApiError(response, 'Failed to fetch note images');
      return await response.json();
    } catch (error) {
      console.error('Error fetching note images:', error);
      throw error;
    }
  };

  // Add these functions to notesApi.js

  /**
   * Fetch all subnotes for a specific parent note
   * @param {number} parentNoteId - The ID of the parent note
   * @returns {Promise<Note[]>} Array of subnotes
   */
  export const fetchSubnotes = async (parentNoteId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/notes/${parentNoteId}/subnotes`, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      
      await handleApiError(response, 'Failed to fetch subnotes');
      return await response.json();
    } catch (error) {
      console.error('Error fetching subnotes:', error);
      throw error;
    }
  };

  /**
   * Create a new subnote under a parent note
   * @param {number} parentNoteId - The ID of the parent note
   * @param {Object} noteData - Data for the new subnote
   * @returns {Promise<Note>} The created subnote
   */
  export const createSubnote = async (parentNoteId, userId, defaultCategoryId = null) => {
    
    const newSubnote = {
      subId: userId,
      noteType: 'text',
      title: 'New Subnote',
      note: '',
      tags: '',
      primary_category_id: defaultCategoryId,
      secondary_categories: '[]',
      parent_id: parentNoteId
    };
    console.log("Creating subnote with data:", JSON.stringify(newSubnote, null, 2));
    try {
      const response = await fetch(`${API_BASE_URL}/notes/${parentNoteId}/subnotes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(newSubnote)
      });

      await handleApiError(response, 'Failed to create subnote');
      return await response.json();
    } catch (error) {
      console.error('Error in createSubnote:', error);
      throw error;
    }
  };